import { useMemo } from 'react';

import { useHeadlinePrices } from '../contexts/fill/use-fill-prescription';
import type {
    AuthorizedMedicationInfoFragment,
    UnknownMedicationInfoFragment,
} from '../graphql/generated/graphql';
import type { PriceCardV2Flag } from '../types/non-boolean-flags';

import { useDrugKey } from './use-drug-key';
import { useFlags } from './use-flags';
import type { PrescriptionOffer } from './use-prescription-offers';

export type UsePriceCardContentProps = {
    medication:
        | AuthorizedMedicationInfoFragment
        | UnknownMedicationInfoFragment;
    headlinePriceType?: PrescriptionOffer['headlinePriceType'] | null;
    patientCost?: PrescriptionOffer['patientCost'] | null;
};

export const usePriceCardContent = ({
    medication,
    headlinePriceType,
    patientCost,
}: UsePriceCardContentProps) => {
    const { enablePriceCardV2 } = useFlags();
    const priceCardFlag = enablePriceCardV2 as PriceCardV2Flag;

    const headlinePrices = useHeadlinePrices(medication);
    const drugKey = useDrugKey(medication);

    const cardContentOverride = useMemo(() => {
        if (!headlinePriceType && !patientCost) {
            return null;
        }

        const selectedPrice = headlinePrices.find(
            (price) =>
                price.type === headlinePriceType &&
                (price.value === patientCost || typeof patientCost !== 'number')
        );

        return {
            pricingCard: selectedPrice?.content?.pricingCard ?? null,
            priceNote: selectedPrice?.content?.priceNote ?? null,
        };
    }, [headlinePrices, headlinePriceType, patientCost]);

    if (
        !!drugKey &&
        priceCardFlag.drugKeys.includes(drugKey) &&
        cardContentOverride?.pricingCard
    ) {
        return cardContentOverride;
    }

    return null;
};
